import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Flame, Sparkles, Coffee, Sandwich } from "lucide-react";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import Marquee from "../components/Marquee";
import SectionHeading from "../components/SectionHeading";
import { IMG, PACK_BAG, PACK_BOX } from "../lib/media";
import { ORDER_ONLINE_URL } from "../lib/links";

const CATEGORIES = [
  { name: "All Day Breakfast & Freshly Cut Sandwiches", desc: "Thick-cut sandwiches, breakfast rolls and toasted muffins, made with fresh bread and packed with delicious fillings — to order, exactly how you like them.", img: IMG.breakfast_bap, to: "/menu#breakfast" },
  { name: "Fresh Bakery", desc: "Traditionally baked bread & rolls with no unnecessary additives — unlike your average supermarket loaf — prepared with quality ingredients.", img: IMG.pastries_mixed, to: "/menu#bread" },
  { name: "Sweet Treats", desc: "Artisan bakes, bakery favourites and locally loved sweet treats made fresh daily.", img: IMG.cake_slice, to: "/menu#cakes" },
  { name: "Hot Kitchen", desc: "Juicy grass-fed beef smash burgers, 24-hour buttermilk crispy chicken burgers, wings & strips, loaded bowls and flavour-packed sauces. Proper takeaway food.", img: IMG.burger_dark, to: "/menu#smash-burgers" },
  { name: "Indian Street Food", desc: "Small but powerful menu, big flavour, modest heat — all made to secret traditional recipes. Authentic, bold, unforgettable. Trust us.", img: IMG.indian_street, to: "/menu#indian-street" },
  { name: "Coffee & Cold Drinks", desc: "Speciality bean-to-cup coffees and a wide range of chilled drinks to satisfy every taste and quench any thirst.", img: IMG.coffee_pour, to: "/menu#drinks" },
];

const SIGNATURE = [
  { name: "Big Breakfast Toasted Muffin", tag: "House Favourite", img: IMG.breakfast_muffin },
  { name: "Smashzilla — 4 Patty Smash", tag: "House Favourite", img: IMG.burger_dark },
  { name: "Crispy Dipped BBQ Wings", tag: "Spicy", img: IMG.fried_chicken },
  { name: "Lamb Curry Rice Bowl", tag: "Spicy", img: IMG.indian_street },
  { name: "Cheese & Ham Ploughman's", tag: "Fresh Daily", img: IMG.sandwich },
  { name: "The Chicken or the Egg?", tag: "House Favourite", img: IMG.burger_board },
];

const PILLARS = [
  { icon: Sparkles, title: "Fresh every day", body: "Breads, pastries, cakes and treats in fresh — ready the moment we open." },
  { icon: Flame, title: "Big, bold flavour", body: "Bakery craft meets street-food energy. Nothing boring." },
  { icon: Sandwich, title: "Built for now", body: "Fast service, grab-and-go, order ahead. Designed for how you actually eat." },
  { icon: Coffee, title: "Local heart", body: "Made in St Neots. Supplying the whole region." },
];

export default function Home() {
  const ref = useReveal();
  useSEO({
    title: "SugaSpice Bakery & Kitchen — Fresh Bakes. Big Flavour. St Neots.",
    description:
      "Premium artisan bakery, hot kitchen, smash burgers, fried chicken and Indian street food. Now open in St Neots, Cambridgeshire. Order online or visit us.",
    image: "/brand/sugaspice-roundel.png",
  });

  return (
    <div ref={ref} className="relative">
      {/* HERO ------------------------------------------------------------ */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-4 md:pt-6 lg:pt-4 pb-12 md:pb-16 lg:pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start lg:items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A]/15 bg-[#F7F5F0] px-4 py-2 text-[11px] tracking-[0.25em] uppercase">
                <span className="h-2 w-2 rounded-full bg-[#C8102E] animate-pulse" />
                Now open in St Neots
              </div>
              <h1 className="mt-5 font-display text-[clamp(2.8rem,6.5vw,6.5rem)] leading-[0.88] tracking-[-0.02em] uppercase">
                Fresh bakes.<br />
                Big flavour.<br />
                <span className="text-[#C8102E]">Made to crave.</span>
              </h1>
              <p className="mt-5 max-w-xl text-sm md:text-base text-[#3a3a3a] leading-relaxed">
                From stacked breakfast baps & toasted muffins and croissants, freshly cut sandwiches & rolls,
                to smash burgers, fried chicken burgers, wings & strips, and bold secret-recipe Indian street
                food — SugaSpice Bakery & Kitchen is a new kind of eatery for St Neots.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={ORDER_ONLINE_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-order-cta" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]">
                  Order Online <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link to="/menu" data-testid="hero-menu-cta" className="btn-pill border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F7F5F0]">
                  Explore the Menu
                </Link>
              </div>
              <div className="mt-7 flex items-center gap-5 text-[11px] tracking-[0.18em] uppercase text-[#5C5C5C]">
                <div><span className="block font-display text-xl text-[#1A1A1A]">Many</span>wholesale partners</div>
                <span className="h-7 w-px bg-[#D1C9BE]" />
                <div><span className="block font-display text-xl text-[#1A1A1A]">14</span>menu categories</div>
                <span className="h-7 w-px bg-[#D1C9BE]" />
                <div><span className="block font-display text-xl text-[#1A1A1A]">Fresh</span>every day</div>
              </div>
            </div>

            <div className="lg:col-span-5 relative h-[380px] md:h-[480px] lg:h-[520px] lg:-mt-4 fade-up">
              <div className="absolute top-0 left-0 w-[68%] h-[60%] rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.18)] rotate-[-2deg]">
                <img fetchpriority="high" decoding="async" src={IMG.hero_pastries} alt="Golden chocolate croissants" className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-0 right-0 w-[72%] h-[62%] rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.22)] rotate-[3deg]">
                <img decoding="async" src={IMG.burger_dark} alt="Smash burger" className="w-full h-full object-cover" />
              </div>
              <div className="absolute top-[42%] right-[6%] w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#C8102E] text-[#F7F5F0] flex flex-col items-center justify-center text-center shadow-[0_20px_40px_rgba(200,16,46,0.4)] rotate-[-8deg]">
                <span className="font-display text-[10px] tracking-[0.2em] uppercase leading-none">Open</span>
                <span className="font-display text-xl md:text-2xl leading-none mt-1">Now</span>
                <span className="font-display text-[9px] tracking-[0.2em] uppercase mt-1">St Neots</span>
              </div>
            </div>
          </div>
        </div>
        <Marquee items={["Town Centre St Neots", "Freshly Made Sandwiches", "Smash Burgers", "Crispy Chicken Burgers", "Dipped Wings & Strips", "Indulgent Cakes & Doughnuts", "All Day Breakfast Rolls", "Hot & Cold Counter", "Real Coffee & Cold Drinks", "Takeaway & Delivery"]} theme="dark" />

        {/* Four-pillar strip ------------------------------------------- */}
        <div className="bg-[#F7F5F0] border-b border-[#D1C9BE]/60">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8" data-testid="hero-pillar-strip">
            {[
              ["Fresh Bread", "In Daily"],
              ["Vibrant", "Hot & Cold Menu"],
              ["Indulgent", "Sweet Creations"],
              ["Premium", "Ingredients"],
            ].map(([top, bottom], i) => (
              <div key={i} className="flex items-start gap-4 fade-up" style={{ transitionDelay: `${i * 60}ms` }}>
                <span className="font-display text-3xl md:text-4xl text-[#C8102E] leading-none">0{i + 1}</span>
                <div>
                  <p className="font-display text-base md:text-lg uppercase tracking-tight leading-tight">{top}</p>
                  <p className="text-xs md:text-sm text-[#5C5C5C] mt-1 uppercase tracking-[0.12em]">{bottom}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO ------------------------------------------------------ */}
      <section className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 fade-up">
            <div className="md:col-span-7">
              <SectionHeading
                eyebrow="What we do"
                title="One love. Six obsessions."
                intro="SugaSpice isn't just food. It's the whole craving — traditional baking, no-nonsense ingredients, properly hot food, deeply flavoured. Designed for the way people actually eat now."
              />
            </div>
            <div className="md:col-span-5 md:pt-12">
              <Link to="/menu" className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.16em] uppercase hover:text-[#C8102E] transition-colors" data-testid="home-explore-menu">
                Explore full menu <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {CATEGORIES.map((c, i) => (
              <Link
                key={c.name}
                to={c.to}
                data-testid={`category-tile-${i}`}
                className={`group fade-up relative overflow-hidden rounded-3xl border border-[#D1C9BE]/60 bg-white aspect-[4/5] ${
                  i % 2 ? "lg:translate-y-8" : ""
                }`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <img loading="lazy" decoding="async" src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/30 to-transparent" />
                <div className="absolute inset-0 p-7 flex flex-col justify-end text-[#F7F5F0]">
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight uppercase">{c.name}</h3>
                  <p className="mt-2 text-sm text-[#E2DDD3] max-w-xs">{c.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase">
                    See more <ChevronRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE CRAVINGS --------------------------------------------- */}
      <section className="py-24 md:py-32 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 fade-up">
            <SectionHeading
              eyebrow="Signature cravings"
              title="The stuff people dream about."
              intro="A taste of what's on the counter — get your mouth watering. Can't get to us? Don't worry, order direct for local delivery. Pricing in store and online."
              invert
            />
            <a href={ORDER_ONLINE_URL} target="_blank" rel="noopener noreferrer" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] w-fit" data-testid="signature-order-cta">
              Order Online <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-8">
            {SIGNATURE.map((s, i) => (
              <div
                key={s.name}
                className="group fade-up relative aspect-square overflow-hidden rounded-3xl border border-white/10"
                style={{ transitionDelay: `${i * 50}ms` }}
                data-testid={`signature-card-${i}`}
              >
                <img loading="lazy" decoding="async" src={s.img} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#F7F5F0] text-[#1A1A1A] text-[9px] sm:text-[10px] tracking-[0.16em] sm:tracking-[0.2em] uppercase font-semibold z-10">
                  {s.tag}
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/70 to-transparent p-4 pt-12 sm:p-5 sm:pt-14">
                  <h4 className="font-display text-sm sm:text-lg md:text-xl tracking-tight uppercase leading-[1.1]">{s.name}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SUGASPICE --------------------------------------------------- */}
      <section className="py-24 md:py-32 bg-[#E8E3DB]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 fade-up">
              <SectionHeading
                eyebrow="Why SugaSpice"
                title="A bakery built to do more."
                intro="We're not picking between pastries and proper food. We do both — and we do them seriously well."
              />
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PILLARS.map((p, i) => (
                <div key={p.title} className="fade-up bg-[#F7F5F0] rounded-3xl p-7 border border-[#D1C9BE]/60" style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="h-11 w-11 rounded-full bg-[#1A1A1A] text-[#F7F5F0] flex items-center justify-center mb-5">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h4 className="font-display text-xl md:text-2xl tracking-tight uppercase">{p.title}</h4>
                  <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STORE EXPERIENCE ----------------------------------------------- */}
      <section className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 relative h-[460px] md:h-[600px] fade-up">
              <div className="absolute top-0 left-0 w-[78%] h-[70%] rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.16)]">
                <img loading="lazy" decoding="async" src={IMG.interior_main} alt="SugaSpice bakery interior" className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-0 right-0 w-[55%] h-[50%] rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.2)] border-8 border-[#F7F5F0]">
                <img loading="lazy" decoding="async" src={IMG.interior_wood} alt="Bakery wood detail" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-5 fade-up">
              <SectionHeading
                eyebrow="The space"
                title="Modern tradition. Warm welcome."
                intro="A not-so-secret hideaway. Deep burgundy counters, warm lighting, glowing hot & cold grab-and-go counters and seamless ordering — the best new takeaway in town."
              />
              <p className="mt-5 text-sm text-[#5C5C5C] leading-relaxed max-w-md">
                Find us on Moore's Walk, off the High Street — between Moore's Walk Garden (in front of the Church) and the old Barclays building.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/visit" className="btn-pill bg-[#1A1A1A] text-[#F7F5F0] hover:bg-[#333]" data-testid="store-visit-cta">
                  Plan a Visit
                </Link>
                <Link to="/about" className="btn-pill border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F7F5F0]">
                  Our Story
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ORDER DIRECT --------------------------------------------------- */}
      <section className="py-24 md:py-32 bg-[#C8102E] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#F7F5F0]/80">Order direct</span>
              <h2 className="font-display mt-4 text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight uppercase">
                Skip the apps.<br /> Order direct.
              </h2>
              <p className="mt-6 max-w-xl text-[#F7F5F0]/85 text-lg leading-relaxed">
                Save money — order direct. Fresher food, better deals, and more love going back into a local independent. We're launching collection and delivery direct from the SugaSpice site, using trusted delivery partners to get your favourites to your door.
              </p>
              <p className="mt-4 max-w-xl text-[#F7F5F0]/70 text-sm leading-relaxed">
                Don't like saving money? Don't worry — we're on the popular food-delivery apps too.
              </p>
              <a href={ORDER_ONLINE_URL} target="_blank" rel="noopener noreferrer" className="btn-pill mt-8 bg-[#F7F5F0] text-[#1A1A1A] hover:bg-white" data-testid="order-direct-cta">
                Start an Order <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div className="lg:col-span-5 fade-up">
              <img loading="lazy" decoding="async" src={PACK_BAG} alt="SugaSpice bakery bag" className="w-full rounded-3xl shadow-[0_30px_60px_rgba(0,0,0,0.25)]" />
            </div>
          </div>
        </div>
      </section>

      {/* WHOLESALE TEASER ---------------------------------------------- */}
      <section className="py-24 md:py-32 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1 fade-up">
              <img loading="lazy" decoding="async" src={PACK_BOX} alt="SugaSpice pastry box" className="w-full rounded-3xl shadow-[0_30px_60px_rgba(0,0,0,0.35)]" />
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2 fade-up">
              <SectionHeading
                eyebrow="For business"
                title="Own a shop, café or forecourt? Grow your business — stock SugaSpice."
                intro="Fresh bread, sandwiches and grab-and-go food supply for local businesses across St Neots, Cambridgeshire and Huntingdonshire."
                invert
              />
              <div className="mt-6 flex flex-wrap gap-2">
                {["Sale or Return", "Refrigerated Daily Deliveries", "Returns Handled", "Real Margin", "Quality Fresh Products", "Premium Packaging"].map((t) => (
                  <span key={t} className="text-[10px] tracking-[0.18em] uppercase font-semibold px-3 py-1.5 rounded-full bg-[#F7F5F0]/10 text-[#F7F5F0]/80 border border-[#F7F5F0]/15">{t}</span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/wholesale" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="wholesale-teaser-cta">
                  Explore Wholesale <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/wholesale#enquire" className="btn-pill border-2 border-[#F7F5F0]/40 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1A1A]" data-testid="wholesale-teaser-enquiry">
                  Make an Enquiry
                </Link>
              </div>
              <p className="mt-8 text-sm text-[#A3A3A3] tracking-[0.16em] uppercase">
                Already trusted by many local business partners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FRESH FROM INSTAGRAM ------------------------------------------ */}
      <section className="py-24 md:py-28 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 fade-up">
            <SectionHeading eyebrow="Fresh from SugaSpice" title="On the gram." />
            <a href="#" className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.16em] uppercase hover:text-[#C8102E] transition-colors">
              @sugaspice <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
            {[IMG.pastries_plate, IMG.burger_board, IMG.indian_street, IMG.fried_chicken, IMG.cake_slice, IMG.coffee_pour, IMG.cookies, IMG.bread_artisan, IMG.sandwich, IMG.pastries_mixed, IMG.interior_wood, IMG.hero_pastries].map((src, i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-2xl fade-up" style={{ transitionDelay: `${i * 30}ms` }}>
                <img loading="lazy" decoding="async" src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA ------------------------------------------------------ */}
      <section className="py-24 md:py-32 bg-[#E8E3DB]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 text-center fade-up">
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight uppercase mx-auto max-w-5xl">
            Taste what St Neots <span className="text-[#C8102E]">has been waiting for.</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/visit" className="btn-pill bg-[#1A1A1A] text-[#F7F5F0] hover:bg-[#333]" data-testid="final-visit-cta">Visit the Bakery</Link>
            <a href={ORDER_ONLINE_URL} target="_blank" rel="noopener noreferrer" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="final-order-cta">Order Online</a>
          </div>
        </div>
      </section>
    </div>
  );
}
